import { CandidateRegistrationDtoClient } from "@/api/candidate/dto/candidate-registration.dto";
import { DelegationStatus } from "@/api/enums/DelegationStatus";
import { PayementStatus } from "@/api/enums/PayementStatus";
import { Role } from "@/api/enums/Role";
import { ValidationStatus } from "@/api/enums/ValidationStatus";
import { PayementUtil } from "./payement-util";

export class RegistrationUtil {
  static isCompleted(registration: CandidateRegistrationDtoClient) {
    const { formationCertificatesStatus, reducedPriceStatus, preTestStatus } =
      registration;

    return (
      formationCertificatesStatus === ValidationStatus.VALIDATED &&
      [ValidationStatus.VALIDATED, ValidationStatus.UNAVAILABLE].includes(
        reducedPriceStatus,
      ) &&
      preTestStatus === ValidationStatus.VALIDATED &&
      PayementUtil.isPaid(registration.payementStatus)
    );
  }

  static currentDebitNote = (registration: CandidateRegistrationDtoClient) => {
    const {
      debitCompanyPaidFile,
      debitCompanyUnpaidFile,
      debitPersonalPaidFile,
      debitPersonalUnpaidFile,
    } = registration;

    return RegistrationUtil.usesCompanyDebit(registration)
      ? PayementUtil.delegationPaid(registration.payementStatus)
        ? debitCompanyPaidFile
        : debitCompanyUnpaidFile
      : PayementUtil.personalPaid(registration.payementStatus)
        ? debitPersonalPaidFile
        : debitPersonalUnpaidFile;
  };

  static candidateCanPay = (registration: CandidateRegistrationDtoClient) => {
    const { delegation, payementStatus } = registration;

    const onGoingDelegation =
      delegation && delegation.status !== DelegationStatus.REFUSED;
    const payementIsWaiting =
      payementStatus === PayementStatus.PERSONAL_WAITING_FOR_VALIDATION;
    const isPaid = PayementUtil.isPaid(payementStatus);

    return !isPaid && !onGoingDelegation && !payementIsWaiting;
  };

  static hasNonRefusedDelegation = (
    registration: CandidateRegistrationDtoClient,
  ) => {
    return (
      registration.delegation &&
      registration.delegation.status !== DelegationStatus.REFUSED
    );
  };

  // must stay in sync with the back-end debit note branch in documents.service.ts
  static usesCompanyDebit = (registration: CandidateRegistrationDtoClient) => {
    return (
      !!registration.delegation &&
      registration.delegation.status !== DelegationStatus.REFUSED &&
      registration.delegation.companyHasAccount
    );
  };

  static needsBillingAddress = (
    registration: CandidateRegistrationDtoClient,
    payer: Role,
  ) => {
    return (
      payer !== Role.COMPANY || !RegistrationUtil.usesCompanyDebit(registration)
    );
  };

  // must stay in sync with the back-end guard in payement.service.ts
  static missingBillingAddress = (
    registration: CandidateRegistrationDtoClient,
    payer: Role,
  ) =>
    !registration.billingAddress &&
    RegistrationUtil.needsBillingAddress(registration, payer);

  static paidDebitNote = (registration: CandidateRegistrationDtoClient) => {
    return RegistrationUtil.usesCompanyDebit(registration)
      ? registration.debitCompanyPaidFile
      : registration.debitPersonalPaidFile;
  };

  static unpaidDebitNote = (registration: CandidateRegistrationDtoClient) => {
    return RegistrationUtil.usesCompanyDebit(registration)
      ? registration.debitCompanyUnpaidFile
      : registration.debitPersonalUnpaidFile;
  };
}
